import EventsManager from '../../core/EventsManager'
import GroupManager from '../../core/GroupManager'
import Logger from '@nematis/js-snippets/assets/js/logging/debug-logger'

export const Etat = {
  wait: 'wait',
  enabled: 'true',
  denied: 'false'
}
export default class ServiceAbstract {
  constructor(parameters, CookieManager) {
    if (this.constructor === ServiceAbstract) {
      throw new TypeError('Abstract class "ServiceAbstract" cannot be instantiated directly')
    }
    this.etat = Etat.wait
    this.CookieManager = CookieManager
    this.GroupManager = new GroupManager()
    this.parameters = parameters
    this.debug = parameters.debug || false
    this.manager = parameters.manager || false

    // on a besoin du consentement par defaut
    this.needConsent = typeof parameters.needConsent !== 'undefined' ? parameters.needConsent : true

    // Si on veut obliger a surcharger des methodes
    // this.parse(path);
  }

  /**
   * Auto enable if consent not need
   */
  checkConsentNeeded() {
    // consent auto si pas encore de réponse
    if (!this.needConsent && this.getStatus() == Etat.wait) {
      this.setStatus(Etat.enabled)
      this.CookieManager.setService(this)
    }
  }

  getStatus() {
    return this.etat
  }

  async setStatus(status) {
    if (this.etat == status) {
      return
    }

    if (Etat.denied == status && this.etat != status) {
      // on vide les cookies relatifs
      this.CookieManager.purgeService(this)
    }

    this.etat = status

    switch (status) {
      case Etat.wait:
        await this.callWait()
        break
      case Etat.enabled:
        await this.callWrite()
        break
      case Etat.denied:
        await this.callFallback()
        break
      default:
        break
    }
  }

  hasFunction(functionName) {
    return typeof this[functionName] === 'function'
  }

  async callInit() {
    // prioritize manager's initService function (if any)
    if (this.hasManager()) {
      const manager = await this.getManager()
      if (manager && manager.hasInitServiceFunction()) {
        manager.initService(this)
        return
      }
    }

    // fallback to service's write method (if any)
    if (this.hasInitFunction()) {
      this.init()
    }
  }

  hasInitFunction() {
    return this.hasFunction('init')
  }

  async callWrite() {
    // prioritize manager's write function (if any)
    if (this.hasManager()) {
      const manager = await this.getManager()
      if (manager && manager.hasWriteFunction()) {
        manager.write(this)
        return
      }
    }

    // fallback to service's write method (if any)
    if (this.hasWriteFunction()) {
      this.write()
    }
  }

  hasWriteFunction() {
    return this.hasFunction('write')
  }

  async callFallback() {
    // prioritize manager's fallback function (if any)
    if (this.hasManager()) {
      const manager = await this.getManager()
      if (manager && manager.hasFallbackFunction()) {
        manager.fallback(this)
        return
      }
    }

    // fallback to service's fallback method (if any)
    if (this.hasFallbackFunction()) {
      this.fallback()
    }
  }

  hasFallbackFunction() {
    return this.hasFunction('fallback')
  }

  async callWait() {
    // prioritize manager's wait function (if any)
    if (this.hasManager()) {
      const manager = await this.getManager()
      if (manager && manager.hasWaitFunction()) {
        manager.wait(this)
        return
      }
    }

    // fallback to service's wait method (if any)
    if (this.hasWaitFunction()) {
      this.wait()
    }
  }

  hasWaitFunction() {
    return this.hasFunction('wait')
  }

  enable() {
    this.setStatus(Etat.enabled)
    this.CookieManager.setService(this)
    this.setCookie()
  }

  deny() {
    if (typeof this.beforeDeny === 'function') {
      this.beforeDeny()
    }

    this.setStatus(Etat.denied)
    this.CookieManager.setService(this)
    this.setCookie()

    if (typeof this.afterDeny === 'function') {
      this.afterDeny()
    }
  }

  isEnabled() {
    return this.getStatus() == Etat.enabled
  }

  isDenied() {
    return this.getStatus() == Etat.denied
  }

  hasManager() {
    return this.getManagerName() !== false
  }

  getManagerName() {
    return this.manager
  }

  async getManager() {
    return await this.getRousquilleServiceManager().getManager(this.getManagerName())
  }

  getRousquilleServiceManager() {
    return window.rousquille.SM
  }

  // override this method to return a relevant id for this service (UA-XXXXX for Analytics, Facebook page id and so on)
  getUniqueIdentifier() {
    return false
  }

  setCookie() {
    // on tag le status du cookie global
    this.CookieManager.enable()
  }

  get(name) {
    if (!this.has(name)) {
      throw new Error(`Property ${name} not found`)
    }
    return this.properties[name]
  }

  getGroup(key) {
    return this.GroupManager.getGroup(key)
  }

  has(name) {
    return name in this.properties
  }

  parse(path) {
    // Function to implement in child class
    throw new Error('You must implement this function')
  }

  getEventKey() {
    return this.key.toLowerCase()
  }

  addScript(url, id, callback, execute, attrName, attrVal) {
    let done = false
    let script

    if (execute === false) {
      if (typeof callback === 'function') {
        callback()
      }
    } else {
      script = document.createElement('script')
      script.type = 'text/javascript'
      script.id = id !== undefined ? id : ''
      script.async = true
      script.src = url

      if (attrName !== undefined && attrVal !== undefined) {
        script.setAttribute(attrName, attrVal)
      }

      if (typeof callback === 'function') {
        const service = this
        script.onreadystatechange = script.onload = function () {
          const state = script.readyState
          if (!done && (!state || /loaded|complete/.test(state))) {
            done = true

            service.sendCustomEvent(`rsq.service.${service.getEventKey()}.callback.triggered`)
            callback()
          }
        }
      }

      this.sendCustomEvent(`rsq.service.${this.getEventKey()}.script.added`)
      document.getElementsByTagName('head')[0].appendChild(script)
    }
  }

  extend(a, b) {
    let prop
    for (prop in b) {
      if (b.hasOwnProperty(prop)) {
        a[prop] = b[prop]
      }
    }
  }

  sendCustomEvent(eventName, extraDetail = {}) {
    // always add current service to event detail for convenience
    const detail = {
      service: this
    }

    // allow passing extra parameters to detail property
    this.extend(detail, extraDetail)

    // add debugging info to extra detail
    const uniqueIdentifier = this.getUniqueIdentifier()
    const hasExtraDetail = Object.keys(extraDetail).length
    if (uniqueIdentifier && hasExtraDetail) {
      this.extend(extraDetail, {
        uniqueIdentifier
      })
    }

    // effectively send event
    EventsManager.dispatchEvent(eventName, {
      detail
    })

    // additional log for specific events
    const matches = eventName.match(/^(?:((?!content).)*)\.(waiting|initialized|ready|disabled|callback\.triggered|script\.added)$/)
    if (matches && matches.length === 3) {
      const eventMessage = matches[2].replace('.', ' ')
      Logger.log(`${this.name}: ${eventMessage}`)

      // extra log (for debugging purpose)
      if (hasExtraDetail) {
        Logger.log(extraDetail)
      }
    }
  }
}
